/**
 * iter109 — Personnel Quick Action modal.
 *
 * Central component used by both:
 *   - /personnel page "+ İşlem Ekle" button
 *   - Dashboard PERSONEL widget "+ İşlem Ekle"
 * So multiple copies of this workflow do NOT diverge (spec §25).
 *
 * Kinds: DAILY_LEAVE, ANNUAL_LEAVE, REPORT, OVERTIME_NORMAL, OVERTIME_HOLIDAY.
 * Multi-employee checkbox selection; one backend movement per employee.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { Modal, Btn, Input, Select, Textarea, NumInput } from "@/components/UI";
import { UserCheck, Search, Check } from "lucide-react";
import { todayTR } from "@/lib/date";

const KINDS = [
  { k: "DAILY_LEAVE", l: "Günlük İzin" },
  { k: "ANNUAL_LEAVE", l: "Yıllık İzin" },
  { k: "REPORT", l: "Rapor / İstirahat" },
  { k: "OVERTIME_NORMAL", l: "Normal Mesai" },
  { k: "OVERTIME_HOLIDAY", l: "Resmî Tatil Mesaisi" },
  { k: "ADVANCE", l: "Avans" },
  { k: "BONUS", l: "Prim / Ek Ödeme" },
];

const today = () => todayTR();

export default function PersonnelQuickAction({ open, onClose, onSaved, defaultKind, preselectedEmployeeIds }) {
  const [kind, setKind] = useState(defaultKind || "DAILY_LEAVE");
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");
  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState("");
  const [days, setDays] = useState(1);
  const [amountTry, setAmountTry] = useState(0);
  const [deductFromSalary, setDeductFromSalary] = useState(false);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/employees?status=active").then(r => setEmployees(r.employees || [])).catch(() => {});
    setKind(defaultKind || "DAILY_LEAVE");
    setSelected(new Set(preselectedEmployeeIds || []));
    setStartDate(today());
    setEndDate("");
    setDays(1);
    setAmountTry(0);
    setDeductFromSalary(false);
    setNote("");
  }, [open, defaultKind, preselectedEmployeeIds]);

  const filtered = useMemo(() => {
    if (!q.trim()) return employees;
    const qt = q.toLocaleLowerCase("tr");
    return employees.filter(e =>
      `${e.first_name} ${e.last_name}`.toLocaleLowerCase("tr").includes(qt) ||
      (e.role || "").toLocaleLowerCase("tr").includes(qt)
    );
  }, [employees, q]);

  const toggle = useCallback((id) => {
    setSelected(s => {
      const nx = new Set(s);
      if (nx.has(id)) nx.delete(id); else nx.add(id);
      return nx;
    });
  }, []);

  const isLeaveOrReport = ["DAILY_LEAVE", "ANNUAL_LEAVE", "REPORT"].includes(kind);
  const isOvertime = kind.startsWith("OVERTIME_");
  const isAmount = kind === "ADVANCE" || kind === "BONUS";

  const save = async () => {
    if (selected.size === 0) return alert("En az bir personel seçin");
    if (isAmount && !(Number(amountTry) > 0)) return alert("Tutar (TRY) 0'dan büyük olmalı");
    setLoading(true);
    try {
      const payload = {
        employee_ids: Array.from(selected),
        kind,
        start_date: startDate,
        end_date: (isLeaveOrReport && endDate) ? endDate : undefined,
        days: isAmount ? undefined
          : ((kind === "ANNUAL_LEAVE" && endDate) ? undefined : Number(days)),
        deduct_from_salary: (kind === "DAILY_LEAVE" || kind === "REPORT") ? !!deductFromSalary : false,
        note: note || undefined,
        amount_try: isAmount ? Number(amountTry) : undefined,
      };
      await api.post("/employees/movements", payload);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      alert("Kayıt hatası: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Personel İşlem Ekle" size="lg">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <label className="block md:col-span-3">
          <span className="text-xs font-medium uppercase tracking-wider text-slate-500 block mb-1">İşlem Türü</span>
          <div className="flex flex-wrap gap-1.5">
            {KINDS.map(k => (
              <button key={k.k} onClick={() => setKind(k.k)}
                data-testid={`qa-kind-${k.k}`}
                className={`px-3 py-1.5 text-xs rounded-full border font-medium transition ${kind === k.k ? "bg-violet-600 border-violet-600 text-white shadow" : "bg-white border-slate-200 text-slate-700 hover:border-violet-300"}`}>
                {k.l}
              </button>
            ))}
          </div>
        </label>
        <Input label="Başlangıç Tarihi" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="qa-start-date"/>
        {kind === "REPORT" && (
          <Input label="Bitiş Tarihi" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} data-testid="qa-end-date"/>
        )}
        {isLeaveOrReport && kind === "ANNUAL_LEAVE" && (
          <Input label="Bitiş Tarihi (opsiyonel)" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} data-testid="qa-end-date"/>
        )}
        {!isAmount && !(kind === "ANNUAL_LEAVE" && endDate) && (
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wider text-slate-500 block mb-1">{isOvertime ? "Süre (saat)" : "Gün Sayısı"}</span>
            <NumInput step={isOvertime ? "0.5" : "1"} min="0" value={days} onChange={e => setDays(parseFloat(e.target.value) || 0)}
              className="w-full px-2.5 py-1.5 text-sm bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
              data-testid="qa-days"/>
          </label>
        )}
        {isAmount && (
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wider text-slate-500 block mb-1">Tutar (TRY)</span>
            <NumInput step="0.01" min="0" value={amountTry}
              onChange={e => setAmountTry(parseFloat(e.target.value) || 0)}
              className="w-full px-2.5 py-1.5 text-sm bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
              data-testid="qa-amount-try"/>
          </label>
        )}
      </div>
      {(kind === "DAILY_LEAVE" || kind === "REPORT") && (
        <label className="flex items-center gap-2 mb-3 text-sm">
          <input type="checkbox" checked={deductFromSalary} onChange={e => setDeductFromSalary(e.target.checked)} data-testid="qa-deduct"/>
          <span>Maaştan kesilsin</span>
        </label>
      )}
      <div className="mb-2">
        <div className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">Personel Seçimi ({selected.size} seçili)</div>
        <div className="relative mb-2">
          <Search size={14} className="absolute left-2 top-2.5 text-slate-400"/>
          <input type="text" placeholder="Personel ara..." value={q} onChange={e => setQ(e.target.value)}
            className="w-full pl-8 pr-2 py-1.5 text-sm border border-slate-200 rounded-md" data-testid="qa-search"/>
        </div>
        <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-md divide-y">
          {filtered.length === 0 && <div className="p-3 text-xs text-slate-400 text-center">Personel bulunamadı</div>}
          {filtered.map(e => (
            <label key={e.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 cursor-pointer text-sm" data-testid={`qa-emp-${e.id}`}>
              <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggle(e.id)}/>
              <UserCheck size={14} className="text-slate-400"/>
              <span className="font-medium">{e.first_name} {e.last_name}</span>
              {e.role && <span className="text-xs text-slate-500">· {e.role}</span>}
            </label>
          ))}
        </div>
      </div>
      <Textarea label="Not (opsiyonel)" value={note} onChange={e => setNote(e.target.value)} rows={2} data-testid="qa-note"/>
      <div className="flex justify-end gap-2 mt-3">
        <Btn variant="secondary" onClick={onClose} data-testid="qa-cancel">Vazgeç</Btn>
        <Btn onClick={save} disabled={loading || selected.size === 0} data-testid="qa-save">
          <Check size={14}/> Kaydet ({selected.size})
        </Btn>
      </div>
    </Modal>
  );
}
